import { isObjectLike } from 'lodash-es';
import type { NumberRange } from '@frontend/types';
import { MockAPIRouter } from './mock-api-router';
import type { HTTPPlugin } from './plugins';

export type FailedErrorResponse = {
  data: Record<string, any> | string | undefined;
};

type RequestInterceptor = (request: Request) => Promise<Request>;
type ResponseInterceptor = (response: Response, requestCopy: Request) => Promise<Response>;
type ErrorInterceptor = (response: Response) => Promise<Response>;

interface InterceptorBase {
  id: string;
  priority?: number;
}

export interface RequestInterceptorEntry extends InterceptorBase {
  interceptor: RequestInterceptor;
}

export interface ResponseInterceptorEntry extends InterceptorBase {
  interceptor: ResponseInterceptor;
}

export interface ErrorInterceptorEntry extends InterceptorBase {
  interceptor: ErrorInterceptor;
}

export interface MockInterceptorEntry extends InterceptorBase {
  mockApiRouter: MockAPIRouter;
}

export class SchemaParamValidationError extends Error {
  cause: { code: string; method: string; status: number; message: string };
  constructor(message: string, cause: { code: string; method: string; status: number; message: string }) {
    super(message);
    this.name = 'SchemaParamValidationError';
    this.cause = cause ?? {
      code: 'SchemaParamValidationError',
      method: 'GET',
      status: 400,
      message: 'Invalid query parameter',
    };
  }
}
export class HttpError extends Error {
  response: Response & FailedErrorResponse;
  data: FailedErrorResponse['data'];
  status: Response['status'];
  statusText: Response['statusText'];
  cause: { code: string; url: string; status: number; message: string; method?: string };

  constructor(
    response: Response & FailedErrorResponse,
    options?: { cause: { code: string; url: string; status: number; message: string; method?: string } }
  ) {
    const { statusText, status } = response;
    super(statusText || String(status), options);

    this.name = 'HttpError';
    this.data = response.data;
    this.message = `${this.displayError()}`;
    this.status = response.status;
    this.statusText = response.statusText;
    this.response = response;
    this.cause = options?.cause || {
      code: 'HttpError',
      url: response.url,
      status: response.status,
      message: 'Error retrieving data',
      method: 'GET',
    };
  }

  private displayError() {
    return typeof this.data === 'object' ? JSON.stringify(this.data) : this.data;
  }

  public toString() {
    const displayError = this.displayError();
    return `Http Error ${this.response.status} ${displayError}`;
  }
}

function assertNonNullish<T>(value: T, message: string): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw Error(message);
  }
}

async function callErrorHandlers(response: Response, errorHandlers: ErrorInterceptor[]) {
  let finalResponse = response;
  for (const errorHandler of errorHandlers) {
    finalResponse = await errorHandler(finalResponse);
  }
  return finalResponse;
}

async function handleErrors(response: Response, errorHandlers: ErrorInterceptor[], request?: Request) {
  await callErrorHandlers(response, errorHandlers);
  /**
   * If none of the error middleware throw an error, we reject with an error here
   */
  const message = await response.text();
  let data;
  try {
    data = JSON.parse(message);
  } catch {
    data = message;
  }

  return Promise.reject(
    new HttpError(
      Object.assign(response, {
        data,
      }),
      {
        cause: {
          code: 'HttpError',
          url: response.url,
          status: response.status,
          message: 'Error retrieving data',
          method: request?.method,
        },
      }
    )
  );
}

type Percentage = NumberRange<1, 101>;
type OnDownloadProgress = (progress: Percentage) => void;
type OnDownloadStream = (progress: number) => void;

/**
 * The purpose of this function is to provide a means for monitoring the progress of a response download.
 * This capability can be leveraged for various purposes, such as displaying a progress bar to indicate
 * the download's progress to the end-user.
 * @param response The response whose body you want to listen to
 * @param onDownloadProgress A callback that will be called on every download progress event
 * @param onDownloadStream A callback that will be called on every download stream event
 */

async function handleDownload(
  response: Response,
  onDownloadProgress?: OnDownloadProgress,
  onDownloadStream?: OnDownloadStream
) {
  if (!onDownloadProgress && !onDownloadStream) return response;

  if (!response.body) {
    throw Error('ReadableStream not yet supported in this browser.');
  }

  const contentEncoding = response.headers.get('content-encoding');
  const contentLength = response.headers.get(contentEncoding ? 'x-file-size' : 'content-length');

  assertNonNullish(contentLength, 'Response size header unavailable');

  const total = parseInt(contentLength, 10);
  let loaded = 0;

  return new Response(
    new ReadableStream({
      start(controller) {
        assertNonNullish(response.body, 'HTTP response has no body or is null');
        const reader = response.body.getReader();

        read();
        function read() {
          reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }
              const currentByteLength = loaded + value.byteLength;
              const progress = Math.round((currentByteLength / total) * 100) as Percentage;
              if (Math.round((loaded / total) * 100) !== progress) {
                onDownloadProgress?.(progress);
              }
              loaded = currentByteLength;
              onDownloadStream?.(currentByteLength);
              controller.enqueue(value);
              read();
            })
            .catch((error) => {
              controller.error(error);
              console.error(error);
            });
        }
      },
    })
  );
}

async function handleReturn(response: Response, responseType?: ResponseType) {
  if (responseType === 'json') {
    /**
     * using JSON.parse allows an empty response body to be returned as ''
     */
    return response.text().then((res) => (res ? JSON.parse(res) : res));
  }

  if (responseType === 'blob') {
    return response.blob();
  }

  if (responseType === 'arrayBuffer') {
    return response.arrayBuffer();
  }

  if (responseType === 'text') {
    return response.text();
  }

  if (responseType === 'none') {
    return response;
  }
}

async function fetchData({
  responseType,
  request,
  onDownloadProgress,
  onDownloadStream,
  errorInterceptors = [],
  responseInterceptors = [],
}: {
  responseType: ResponseType;
  request: Request;
  onDownloadProgress?: OnDownloadProgress;
  onDownloadStream?: OnDownloadStream;
  responseInterceptors?: ResponseInterceptorEntry[];
  errorInterceptors?: ErrorInterceptorEntry[];
}) {
  const requestCopy = request.clone();
  let response = await fetch(request).catch((error) => {
    throw new Error(`Failed to fetch data: ${request.url}`, {
      cause: error,
    });
  });

  // Apply response interceptors
  for (const { interceptor } of responseInterceptors) {
    response = await interceptor(response, requestCopy);
  }

  if (!response.ok) {
    return handleErrors(
      response,
      errorInterceptors.map(({ interceptor }) => interceptor),
      requestCopy
    );
  }

  return handleDownload(response, onDownloadProgress, onDownloadStream).then((downloadResponse) =>
    handleReturn(downloadResponse, responseType)
  );
}

const LOCATION_ID_HEADER = 'Location-Id';
const AUTHORIZATION_HEADER = 'Authorization';

export interface IHTTP {
  baseUrl?: string;
  headers?: Record<string, string>;
  getAuthorizationHeader?: () => string | undefined;
  children?: React.ReactNode;
}

type ResponseType = 'blob' | 'arrayBuffer' | 'json' | 'text' | 'none';

export interface Options {
  skipValidation?: boolean;
  headers?: RequestInit['headers'];
  responseType?: ResponseType;
  //... add options as needed ...//
  signal?: RequestInit['signal'];
  params?: Record<string, any>;
  onDownloadProgress?: OnDownloadProgress;
  onDownloadStream?: OnDownloadStream;
  /** Useful for setting the baseUrl when the config isn't available (like for unit tests) */
  baseUrl?: string;
  /** Skip the retry logic, if any is configured */
  skipRetry?: boolean;
}

interface PollingOptions {
  /**
   * Time interval (in milliseconds) between each polling attempt.
   * @default 1000
   */
  interval?: number;

  /**
   * Maximum number of polling attempts.
   * @default 15
   */
  maxAttempts?: number;

  /**
   * Maximum number of retries after encountering an error.
   * @default 3
   */
  maxRetries?: number;

  /**
   * Multiplier to increase the interval between retries.
   * @default 1
   */
  backoffMultiplier?: number;
}

interface RequestParams {
  url: string;
  body?: BodyInit;
  options?: Options;
}

interface MakeRequestParams extends RequestParams {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

export type APIResponse<T> = {
  data: T;
};

export function getResponseData<T>(response: APIResponse<T>) {
  return 'data' in response ? response.data : response;
}

type Builder<T, UsedKeys extends keyof T = never> = {
  addParam: <K extends keyof Omit<T, UsedKeys>>(key: K, value: T[K], condition?: boolean) => Builder<T, UsedKeys | K>;
  build: () => Pick<T, UsedKeys>;
};

export class HTTP {
  temporaryHeaders = [] as { id: string; headers: Headers }[];
  headers = new Headers();
  baseUrl = '';
  globalSkipValidation = false;

  requestInterceptors: RequestInterceptorEntry[] = [];
  responseInterceptors: ResponseInterceptorEntry[] = [];
  errorInterceptors: ErrorInterceptorEntry[] = [];
  mockInterceptors: MockInterceptorEntry[] = [];

  private plugins: HTTPPlugin[] = [];
  private getAuthorizationHeader: IHTTP['getAuthorizationHeader'];

  constructor({ baseUrl, headers, getAuthorizationHeader }: IHTTP = {}) {
    if (headers) {
      this.headers = new Headers(headers);
    }

    if (baseUrl) {
      this.baseUrl = baseUrl;
    }

    this.getAuthorizationHeader = getAuthorizationHeader;
  }

  addPlugin = (plugin: HTTPPlugin) => {
    // Remove existing plugin with same id
    this.plugins = this.plugins.filter((p) => p.id !== plugin.id);
    this.plugins.push(plugin);
    plugin.onInit?.();
  };

  removePlugin = (id: string) => {
    const plugin = this.plugins.find((p) => p.id === id);
    if (plugin) {
      plugin.onDestroy?.();
      this.plugins = this.plugins.filter((p) => p.id !== id);
    }
  };

  getPlugin = <T extends HTTPPlugin>(id: string): T | undefined => {
    return this.plugins.find((p) => p.id === id) as T | undefined;
  };

  private notifyPluginsOfHeaderChange = (headerName: string, newValue: string | null, previousValue: string | null) => {
    if (newValue === previousValue) {
      return;
    }

    this.plugins.forEach((plugin) => {
      plugin.onHeaderChange?.(headerName.toLowerCase(), newValue, previousValue);
    });
  };

  private sortInterceptor = (interceptors: InterceptorBase[]) => {
    // Sort by priority (higher numbers execute first)
    interceptors.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  };

  addRequestInterceptor = (interceptorEntry: RequestInterceptorEntry) => {
    const matchedIndex = this.requestInterceptors.findIndex((entry) => entry.id === interceptorEntry.id);

    if (matchedIndex === -1) {
      this.requestInterceptors.push(interceptorEntry);
    } else {
      this.requestInterceptors[matchedIndex] = interceptorEntry;
    }
    this.sortInterceptor(this.requestInterceptors);
  };

  removeRequestInterceptor = (id: string) => {
    this.requestInterceptors = this.requestInterceptors.filter((interceptor) => interceptor.id !== id);
  };

  addResponseInterceptor = (interceptorEntry: ResponseInterceptorEntry) => {
    const matchedIndex = this.responseInterceptors.findIndex((entry) => entry.id === interceptorEntry.id);

    if (matchedIndex === -1) {
      this.responseInterceptors.push(interceptorEntry);
    } else {
      this.responseInterceptors[matchedIndex] = interceptorEntry;
    }

    this.sortInterceptor(this.responseInterceptors);
  };

  removeResponseInterceptor = (id: string) => {
    this.responseInterceptors = this.responseInterceptors.filter((interceptor) => interceptor.id !== id);
  };

  addErrorInterceptor = (interceptorEntry: ErrorInterceptorEntry) => {
    const matchedIndex = this.errorInterceptors.findIndex((entry) => entry.id === interceptorEntry.id);

    if (matchedIndex === -1) {
      this.errorInterceptors.push(interceptorEntry);
    } else {
      this.errorInterceptors[matchedIndex] = interceptorEntry;
    }

    this.sortInterceptor(this.errorInterceptors);
  };

  removeErrorInterceptor = (id: string) => {
    this.errorInterceptors = this.errorInterceptors.filter((interceptor) => interceptor.id !== id);
  };

  addMockInterceptor = (interceptorEntry: MockInterceptorEntry) => {
    const matchedIndex = this.mockInterceptors.findIndex((entry) => entry.id === interceptorEntry.id);

    if (matchedIndex === -1) {
      this.mockInterceptors.push(interceptorEntry);
    } else {
      this.mockInterceptors[matchedIndex] = interceptorEntry;
    }
    this.sortInterceptor(this.mockInterceptors);
  };

  removeMockInterceptor = (id: string) => {
    this.mockInterceptors = this.mockInterceptors.filter((interceptor) => interceptor.id !== id);
  };

  clearAllInterceptors = () => {
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.errorInterceptors = [];
    this.mockInterceptors = [];
  };

  validateHeaders = (headers = this.headers, requiredHeaders = [AUTHORIZATION_HEADER, LOCATION_ID_HEADER]) => {
    requiredHeaders.forEach((header) => {
      if (!headers.has(header)) {
        throw new Error(`${header} not configured`);
      }
    });
  };

  private robustEncode(str: string) {
    const charMap = {
      '!': '%21',
      "'": '%27',
      '(': '%28',
      ')': '%29',
      '~': '%7E',
      '%20': '+',
      '%00': '\x00',
    };

    const replacer = (match: string) => {
      return charMap[match as keyof typeof charMap];
    };

    return encodeURIComponent(str).replace(/[!'()~]|%20|%00/g, replacer);
  }

  private generateQueryParams(params: Record<string, any>) {
    return Object.keys(params)
      .reduce<string[]>((acc, key) => {
        if (params[key] === undefined) {
          return [...acc];
        } else if (Array.isArray(params[key])) {
          return [...acc, ...params[key].map((item: any) => `${this.robustEncode(key)}=${this.robustEncode(item)}`)];
        } else {
          return [...acc, `${this.robustEncode(key)}=${this.robustEncode(params[key])}`];
        }
      }, [])
      .join('&');
  }

  private async applyRequestInterceptors(request: Request): Promise<Request> {
    let interceptedRequest = request;
    for (const { interceptor } of this.requestInterceptors) {
      interceptedRequest = await interceptor(interceptedRequest);
    }
    return interceptedRequest;
  }

  private async applyMockInterceptors(request: Request): Promise<Response | null> {
    for (const { mockApiRouter } of this.mockInterceptors) {
      const response = await mockApiRouter.match(request);
      if (response !== null) {
        return response;
      }
    }
    return null;
  }

  makeRequest = async <T>({ url, method, body, options = {} }: MakeRequestParams): Promise<T> => {
    const {
      headers,
      skipValidation,
      onDownloadProgress,
      onDownloadStream,
      baseUrl,
      skipRetry: _skipRetry, // no-op, retained for compatibility until callers are migrated off it
      ...otherOptions
    } = options;
    const requestHeaders = new Headers(this.headers);
    if (this.getAuthorizationHeader) {
      const authorizationHeader = this.getAuthorizationHeader();
      if (authorizationHeader) {
        requestHeaders.set(AUTHORIZATION_HEADER, authorizationHeader);
      }
      // NOTE if the deprecated setAuthorizationHeader was used, that header will still be set in the request below
      // This means that the client could potentially send a stored token after "canonical auth" (i.e what getWeaveToken() returns) has been cleared.
      // I thought about having an `else { requestHeaders.delete(AUTHORIZATION_HEADER); }` in here but decided against it because it could cause breakage, especially in integration/e2e tests.
    }

    if (headers) {
      if (Array.isArray(headers)) {
        headers.forEach(([key, val]) => {
          if (key.startsWith('-')) {
            requestHeaders.delete(key.slice(1));
          } else {
            requestHeaders.set(key, val);
          }
        });
      } else if (typeof headers === 'object') {
        Object.entries(headers).map(([name, value]) => {
          if (name.startsWith('-')) {
            requestHeaders.delete(name.slice(1));
          } else {
            requestHeaders.set(name, value);
          }
        });
      }
    }

    // all temporary headers are added sequentially, with latest taking precendence
    if (this.temporaryHeaders.length > 0) {
      this.temporaryHeaders.forEach(({ headers: temporaryHeaders }) => {
        temporaryHeaders.forEach((value, key) => {
          requestHeaders.set(key, value);
        });
      });
    }

    if (!skipValidation && !this.globalSkipValidation) {
      this.validateHeaders(requestHeaders);
    }

    let resolvedUrl = url.startsWith('http')
      ? url
      : `${baseUrl || this.baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;

    if (options?.params) {
      resolvedUrl += (url.indexOf('?') === -1 ? '?' : '&') + this.generateQueryParams(options?.params);
      delete options.params;
    }

    let request = new Request(resolvedUrl, {
      headers: requestHeaders,
      method,
      body,
      ...otherOptions,
    });

    // Apply request interceptors
    request = await this.applyRequestInterceptors(request);

    // Check for mock interceptors
    const mockResponse = await this.applyMockInterceptors(request);
    if (mockResponse !== null) {
      if (!mockResponse.ok) {
        return handleErrors(
          mockResponse,
          this.errorInterceptors.map(({ interceptor }) => interceptor),
          request
        );
      }
      return handleReturn(mockResponse, options?.responseType || 'json');
    }

    return fetchData({
      responseType: options?.responseType || 'json',
      request,
      onDownloadProgress,
      onDownloadStream,
      responseInterceptors: this.responseInterceptors,
      errorInterceptors: this.errorInterceptors,
    });
  };

  setBaseUrl = (url: string) => {
    this.baseUrl = url;
  };

  setLocationIdHeader = (id: string | undefined) => {
    const previousValue = this.headers.get(LOCATION_ID_HEADER);

    if (id === null || id === undefined) {
      this.headers.delete(LOCATION_ID_HEADER);
      this.notifyPluginsOfHeaderChange(LOCATION_ID_HEADER, null, previousValue);
    } else {
      this.headers.set(LOCATION_ID_HEADER, id);
      this.notifyPluginsOfHeaderChange(LOCATION_ID_HEADER, id, previousValue);
    }
  };

  getLocationIdHeader = () => this.headers.get(LOCATION_ID_HEADER);

  /** @deprecated Configure getAuthorizationHeader instead of storing Authorization on the HTTP client. In the future, this method will be removed. */
  setAuthorizationHeader = (token: string | undefined, jti?: string) => {
    const previousValue = this.headers.get(AUTHORIZATION_HEADER);

    if (token === null || token === undefined) {
      this.headers.delete(AUTHORIZATION_HEADER);
      this.notifyPluginsOfHeaderChange(AUTHORIZATION_HEADER, null, previousValue);
    } else {
      const newValue = `Bearer ${jti ?? token}`;
      this.headers.set(AUTHORIZATION_HEADER, newValue);
      this.notifyPluginsOfHeaderChange(AUTHORIZATION_HEADER, newValue, previousValue);
    }
  };

  setHeaders = (headers: Record<string, string | null | undefined>) => {
    Object.entries(headers).forEach(([key, value]) => {
      const previousValue = this.headers.get(key);

      if (value === null || value === undefined) {
        this.headers.delete(key);
        if (previousValue !== null) {
          this.notifyPluginsOfHeaderChange(key, null, previousValue);
        }
      } else {
        this.headers.set(key, value);
        this.notifyPluginsOfHeaderChange(key, value, previousValue);
      }
    });
  };

  setGlobalSkipValidation = (globalSkipValidation: boolean) => {
    this.globalSkipValidation = globalSkipValidation;
  };

  setHeader = (key: string, value: string) => {
    const previousValue = this.headers.get(key);
    this.headers.set(key, value);
    this.notifyPluginsOfHeaderChange(key, value, previousValue);
  };

  deleteHeader = (key: string) => {
    const previousValue = this.headers.get(key);
    this.headers.delete(key);
    this.notifyPluginsOfHeaderChange(key, null, previousValue);
  };

  private isValidRequestBody(payload: unknown): payload is XMLHttpRequestBodyInit {
    if (!payload) return false;

    const validPayloadTypes = [ArrayBuffer, Blob, File, URLSearchParams, FormData];

    return (
      typeof payload === 'string' ||
      validPayloadTypes.some((payloadType) => {
        return payload instanceof payloadType;
      })
    );
  }

  paramBuilder<T>(): Builder<T> {
    const object: any = {};

    function addParam<K extends keyof T>(key: K, value: T[K], condition?: boolean): Builder<T, keyof T> {
      if (condition || condition === undefined) {
        object[key] = value;
      }
      return { addParam, build } as Builder<T, keyof T>;
    }

    function build(): Partial<T> {
      return object;
    }

    return { addParam, build };
  }

  get = <T>(url: string, options?: Options): Promise<T> => this.makeRequest<T>({ url, method: 'GET', options });

  publicGet = <T>(url: string, options?: Options): Promise<T> =>
    this.makeRequest<T>({ url, method: 'GET', options: { ...options, skipValidation: true } });

  publicPost = <T, U>(url: string, body: U, options?: Options): Promise<T> =>
    this.post<T, U>(url, body, { ...options, skipValidation: true });

  /**
   * This is a convenience method that extracts the `data` property from the API response
   */
  getData = <T>(url: string, options?: Options): Promise<T> =>
    this.makeRequest<APIResponse<T>>({ url, method: 'GET', options }).then((res) => res.data);

  publicGetData = <T>(url: string, options?: Options): Promise<T> => {
    const opts: Options = { ...options, skipValidation: true };
    return this.getData(url, opts);
  };

  post = <T, U = XMLHttpRequestBodyInit | Record<string, unknown>>(
    url: string,
    body?: U,
    options?: Options
  ): Promise<T> =>
    this.makeRequest<T>({
      url,
      method: 'POST',
      body: this.isValidRequestBody(body) ? body : JSON.stringify(body),
      options,
    });

  put = <T, U = XMLHttpRequestBodyInit | Record<string, unknown>>(
    url: string,
    body?: U,
    options?: Options
  ): Promise<T> =>
    this.makeRequest({
      url,
      body: this.isValidRequestBody(body) ? body : JSON.stringify(body),
      method: 'PUT',
      options,
    });

  patch = <T, U = XMLHttpRequestBodyInit | Record<string, unknown>>(
    url: string,
    body?: U,
    options?: Options
  ): Promise<T> =>
    this.makeRequest({
      url,
      body: this.isValidRequestBody(body) ? body : JSON.stringify(body),
      method: 'PATCH',
      options,
    });

  delete = <T, U = Record<string, unknown>>(url: string, body?: U, options?: Options): Promise<T> =>
    this.makeRequest({
      url,
      body: body && JSON.stringify(body),
      method: 'DELETE',
      options,
    });

  poll = <T>(
    url: string,
    validate: (response: T) => boolean,
    options?: Options,
    pollingOptions?: PollingOptions
  ): Promise<T> => {
    let attempts = 0;
    let retries = 0;
    let interval = pollingOptions?.interval ?? 1000;
    const maxAttempts = pollingOptions?.maxAttempts ?? 15;
    const maxRetries = pollingOptions?.maxRetries ?? 3;
    const backoffMultiplier = pollingOptions?.backoffMultiplier ?? 1;

    return new Promise((resolve, reject) => {
      const pollAgain = async () => {
        try {
          const response = await this.makeRequest<T>({ url, method: 'GET', options });
          attempts++;

          if (validate(response)) {
            resolve(response);
          }
          if (attempts >= maxAttempts) {
            reject(new Error('Max polling attempts exceeded'));
          }

          interval *= backoffMultiplier;
          setTimeout(pollAgain, interval);
        } catch (error) {
          retries++;
          if (retries > maxRetries) {
            reject(error);
          }

          interval *= backoffMultiplier;
          setTimeout(pollAgain, interval);
        }
      };
      pollAgain();
    });
  };

  isHttpError(error: unknown): error is HttpError {
    return isObjectLike(error) && error instanceof HttpError;
  }

  /**
   * ### ⚠️ Warning
   * Do not use this if onDownloadProgress or onDownloadStream is utilized,
   * as the thrown ```DOMException``` error will not propagate in Chrome. This will result
   * in a ***"TypeError: Failed to fetch"*** error.
   *
   * The proper method for determining if a request was cancelled is to examine the
   * "aborted" status of the signal provided to the request.
   */
  isAbortError(error: any): error is DOMException {
    if (error && error.name === 'AbortError') {
      return true;
    }
    return false;
  }
}
